import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import tzLookup from 'tz-lookup';
import { functions } from './firebase';
import { useKnownSwims } from './KnownSwimsContext';

const coordinateFromPoint = (point) => {
  const latitude = point?.latitude ?? point?._latitude;
  const longitude = point?.longitude ?? point?._longitude;
  return Number.isFinite(latitude) && Number.isFinite(longitude) ? { latitude, longitude } : null;
};

const timezoneForCoordinate = (coordinate) => {
  if (!coordinate) return 'Unknown';
  try {
    return tzLookup(coordinate.latitude, coordinate.longitude);
  } catch {
    return 'Unknown';
  }
};

const knownToUserRatio = (zone) => {
  if (zone.userCount === 0) return zone.knownSwimCount > 0 ? Infinity : 0;
  return zone.knownSwimCount / zone.userCount;
};

const KnownSwimsDashboardPage = ({ onFocusTimezone }) => {
  const [knownSwimCount, setKnownSwimCount] = useState(0);
  const [timezones, setTimezones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({ key: 'userCount', direction: 'desc' });
  const { knownSwims, loading: loadingKnownSwims, error: knownSwimsError, ensureKnownSwims } = useKnownSwims();

  useEffect(() => {
    ensureKnownSwims();
  }, [ensureKnownSwims]);

  useEffect(() => {
    const loadUserSwims = async () => {
      setLoading(true);
      setError(null);
      try {
        const getDashboardData = httpsCallable(functions, 'getDashboardData');
        const userResult = await getDashboardData({ days: 455 });
        setTimezones(userResult.data.swims || []);
      } catch (loadError) {
        console.error('Unable to load Known Swims dashboard:', loadError);
        setError('Unable to load Known Swims dashboard.');
      } finally {
        setLoading(false);
      }
    };
    loadUserSwims();
  }, []);

  const timezoneData = useMemo(() => {
    if (loadingKnownSwims || loading) return [];
    const zones = new Map();
    const addToZone = (coordinate, type, userID = null) => {
      const timezone = timezoneForCoordinate(coordinate);
      const zone = zones.get(timezone) || {
        timezone,
        userIDs: new Set(),
        knownSwimCount: 0,
        coordinates: []
      };
      if (type === 'user' && userID) zone.userIDs.add(userID);
      if (type === 'known') zone.knownSwimCount += 1;
      if (coordinate) zone.coordinates.push(coordinate);
      zones.set(timezone, zone);
    };

    timezones.forEach((swim) => addToZone(
      coordinateFromPoint(swim.startCoordinate),
      'user',
      swim.userID
    ));
    knownSwims.forEach((swim) => addToZone(coordinateFromPoint(swim.route?.[0]), 'known'));
    return [...zones.values()].map(({ userIDs, ...zone }) => ({
      ...zone,
      userCount: userIDs.size
    }));
  }, [knownSwims, loading, loadingKnownSwims, timezones]);

  useEffect(() => {
    setKnownSwimCount(knownSwims.length);
  }, [knownSwims]);

  const sortedTimezones = useMemo(() => [...timezoneData].sort((first, second) => {
    const firstValue = sort.key === 'knownToUserRatio' ? knownToUserRatio(first) : first[sort.key];
    const secondValue = sort.key === 'knownToUserRatio' ? knownToUserRatio(second) : second[sort.key];
    const comparison = typeof firstValue === 'string'
      ? firstValue.localeCompare(secondValue)
      : firstValue - secondValue;
    return sort.direction === 'asc' ? comparison : -comparison;
  }), [sort, timezoneData]);

  const toggleSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const formatRatio = (zone) => {
    const ratio = knownToUserRatio(zone);
    return ratio === Infinity ? '∞' : ratio.toFixed(1);
  };

  if (loading || loadingKnownSwims) return <div className="known-swims-dashboard-page">Loading Known Swims coverage...</div>;

  return (
    <section className="known-swims-dashboard-page">
      <h2>Known Swims Dashboard</h2>
      <div className="known-swims-dashboard-count">
        <strong>{knownSwimCount}</strong>
        <span>Known Swims</span>
      </div>
      <p>Compare curated coverage with unique users from the last 15 months.</p>
      {(error || knownSwimsError) && <p className="error">{error || knownSwimsError}</p>}
      <div className="known-swims-timezone-table-wrap">
        <table className="known-swims-timezone-table">
          <thead>
            <tr>
              <th><button onClick={() => toggleSort('timezone')}>Timezone</button></th>
              <th><button onClick={() => toggleSort('userCount')}>Users</button></th>
              <th><button onClick={() => toggleSort('knownSwimCount')}>Known Swims</button></th>
              <th><button onClick={() => toggleSort('knownToUserRatio')}>Known Swims / Users</button></th>
            </tr>
          </thead>
          <tbody>
            {sortedTimezones.map((zone) => (
              <tr key={zone.timezone}>
                <td>
                  <button className="known-swims-timezone-link" onClick={() => onFocusTimezone(zone)}>
                    {zone.timezone}
                  </button>
                </td>
                <td>{zone.userCount}</td>
                <td>{zone.knownSwimCount}</td>
                <td>{formatRatio(zone)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default KnownSwimsDashboardPage;
